import React, { useEffect, useMemo, useState } from "react";
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from "react-native";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { ProductFieldInfoVisibility } from "../../../../../view-models/ProductFieldInfoVisibility";
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";
import DownArrowIcon from "../../../../../../../../assets/images/DownArrowIcon.svg";
import PersonIcon from "../../../../../../../../assets/images/user-round.svg";

interface AdditionalReceiverInformationStepProps {
  onValidationChange?: (valid: boolean) => void;
}

const DROPDOWN_FIELD_LABELS = ["Occupation", "Country of Birth",];

function isDropdownField(label: string): boolean {
  const normalized = (label ?? "").trim().toLowerCase();
  if (!normalized) return false;
  if (normalized.includes("occupation")) return true;
  return DROPDOWN_FIELD_LABELS.some(
    (l) => l.toLowerCase() === normalized || normalized.includes(l.toLowerCase())
  );
}

function isOccupationField(label: string): boolean {
  return (label ?? "").toLowerCase().includes("occupation");
}

function getEnumeratedOptions(field: any): Array<{ value: string; label: string }> {
  const raw = field?.enumeratedValues ?? field?.EnumeratedValues ?? [];
  if (!Array.isArray(raw)) return [];
  return raw
    .map((opt: any) => ({
      value: String(opt?.Value ?? opt?.value ?? ""),
      label: String(opt?.Label ?? opt?.label ?? opt?.Value ?? opt?.value ?? ""),
    }))
    .filter((o) => o.value !== undefined && o.label !== undefined);
}

const AdditionalReceiverInformationStep: React.FC<AdditionalReceiverInformationStepProps> = ({ onValidationChange }) => {
  const missingRequiredFields = useSessionStore((s: SessionState) => (s as any).missingRequiredFields) as any[];
  const setAdditionalFieldValue = useSessionStore((s: SessionState) => (s as any).setAdditionalFieldValue);
  const additionalFieldValues = useSessionStore((s: SessionState) => (s as any).additionalFieldValues) as Record<string, any>;

  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});
  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);

  const requiredFields = useMemo(() => (missingRequiredFields || []).filter((f: any) => (f?.visibility === ProductFieldInfoVisibility.REQ)), [missingRequiredFields]);

  useEffect(() => {
    // validate all fields to determine continue button state
    const errs: Record<string, string> = {};
    for (const f of requiredFields) {
      const key = String(f?.XmlTag ?? f?.xmlTag ?? "");
      const label = String(f?.FieldLabel ?? f?.fieldLabel ?? key);
      const min = Number(f?.FieldMin ?? f?.fieldMin ?? 0);
      const max = Number(f?.FieldMax ?? f?.fieldMax ?? Number.MAX_SAFE_INTEGER);
      const valueRaw = additionalFieldValues?.[key] ?? "";
      const value = String(valueRaw ?? "");
      if (value.length < min) errs[key] = `${label} must be at least ${min} characters`;
      else if (value.length > max) errs[key] = `${label} must be at most ${max} characters`;
      else if (!value) errs[key] = `${label} is required`;
    }
    setLocalErrors(errs);
    onValidationChange?.(Object.keys(errs).length === 0);
  }, [requiredFields, additionalFieldValues]);

  const handleChange = (key: string, text: string) => {
    setAdditionalFieldValue?.(key, text);
  };

  return (
    <View style={styles.container}>
      <Text style={[styles.title, { paddingRight: 100 }]}>We need a few more details</Text>
      {requiredFields.map((f: any, index: number) => {
        const key = String(f?.XmlTag ?? f?.xmlTag ?? "");
        const label = String(f?.FieldLabel ?? f?.fieldLabel ?? key);
        const placeholder = label;
        const value = String((additionalFieldValues?.[key] ?? ""));
        const error = localErrors[key];
        const useDropdown = isDropdownField(label);
        const options = useDropdown ? getEnumeratedOptions(f) : [];

        if (useDropdown && options.length > 0) {
          const selectedOption = options.find((o) => o.value === value);
          const displayText = selectedOption ? selectedOption.label : placeholder;
          const isOpen = openDropdownKey === key;
          return (
            <View key={key} style={styles.fieldWrapper}>
              <View style={styles.dropdownCard}>
                <TouchableOpacity
                  testID={`receiveAdditionalField_${key}_dropdown`}
                  style={styles.dropdownCardHeader}
                  onPress={() => setOpenDropdownKey(isOpen ? null : key)}
                  activeOpacity={0.7}
                >
                  <Text style={[styles.dropdownCardHeaderText, !value && styles.dropdownPlaceholder]} numberOfLines={1}>
                    {displayText}
                  </Text>
                  <View style={styles.dropdownArrowWrap}>
                    <DownArrowIcon width={16} height={16} />
                  </View>
                </TouchableOpacity>
                {isOpen && (
                  <View style={styles.dropdownCardListWrap}>
                    <ScrollView style={styles.dropdownCardScroll} nestedScrollEnabled>
                      {options.map((opt) => (
                        <TouchableOpacity
                          testID={`receiveAdditionalField_${key}_option`}
                          key={opt.value}
                          style={styles.dropdownItem}
                          onPress={() => {
                            setAdditionalFieldValue?.(key, opt.value);
                            setOpenDropdownKey(null);
                          }}
                        >
                          <Text style={styles.dropdownItemText}>{opt.label}</Text>
                        </TouchableOpacity>
                      ))}
                    </ScrollView>
                  </View>
                )}
              </View>
              {!!error && <Text style={styles.error}>{error}</Text>}
            </View>
          );
        }

        return (
          <View key={key} style={styles.fieldWrapper}>
            <Text style={styles.label}>{label}</Text>
            <FloatingTextInputComponent
              testID={`receiveAdditionalField_${key}`}
              label={placeholder}
              value={value}
              onChangeText={(t) => handleChange(key, t)}
              isAutoFocus={false}
            />
            {!!error && <Text style={styles.error}>{error}</Text>}
          </View>
        );
      })}
      {requiredFields.some((f: any) => isOccupationField(String(f?.FieldLabel ?? f?.fieldLabel ?? ""))) && (
        <View style={styles.infoBox}>
          <View style={styles.infoBoxIconWrap}>
            <PersonIcon width={24} height={24} />
          </View>
          <View style={styles.infoBoxContent}>
            <Text style={styles.infoBoxTitle}>Why do we ask these questions?</Text>
            <Text style={styles.infoBoxBody}>
              Regulations require us to verify identity before you send money. This helps prevent identity theft and keep information safe.
            </Text>
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
    paddingHorizontal: 24,
  },
  fieldWrapper: {
    marginBottom: 16
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8,
  },
  dropdownCard: {
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    overflow: "hidden",
  },
  dropdownCardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 12,
    paddingHorizontal: 12,
    backgroundColor: Colors.dropDownBackground
  },
  dropdownCardHeaderText: {
    fontSize: 14,
    color: "#111",
    flex: 1,
  },
  dropdownPlaceholder: {
    color: "#6B7280",
  },
  dropdownArrowWrap: {
    marginLeft: 8,
  },
  dropdownCardListWrap: {
    maxHeight: 240,
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  dropdownCardScroll: {
    maxHeight: 240,
    backgroundColor: Colors.dropDownBackground
  },
  dropdownItem: {
    paddingVertical: 12,
    paddingHorizontal: 12,
  },
  dropdownItemText: {
    fontSize: 14,
    color: "#6B7280",
  },
  label: {
    fontSize: 14,
    color: "#111",
    marginBottom: 6
  },
  infoBox: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 16,
    padding: 12,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#1F1F1F",
    borderRadius: 8,
  },
  infoBoxIconWrap: {
    paddingRight: 16,
    paddingLeft: 4
  },
  infoBoxContent: {
    flex: 1,
  },
  infoBoxTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: "#111",
    marginBottom: 4,
  },
  infoBoxBody: {
    fontSize: 14,
    fontWeight: "400",
    color: "#374151",
    lineHeight: 20,
  },
  error: {
    color: "#E11D48",
    marginTop: 6,
    fontSize: 12
  }
});

export default AdditionalReceiverInformationStep;


